import React, { useEffect, useState } from 'react'
import { AppState, ScrollView, View, Linking, Platform, TouchableOpacity } from 'react-native'
import { useNavigation, useTheme } from '@react-navigation/native'
import { Screen, Header, Text, Icon, Button } from '@components'
import { useLanguage } from '@context'
import {
  useScreen,
  useSubscription,
  usePlanLimits,
  isSubscriptionActivationPending,
  markSubscriptionActivationPending,
} from '@hooks'
import {
  showInfoToast,
  showSuccessToast,
  showErrorToast,
  showToast,
  fromApiDateFormat,
} from '@utils'
import { purchaseManager } from '../../../utils/purchases/purchaseManager'
import { CancelSubscriptionModal } from './components/CancelSubscriptionModal'
import { TierCard } from './components/TierCard'
import { INFINITY_FEATURE_KEYS } from './infinityFeatures'
import { getSubscriptionStyles } from './style'

// After a successful web purchase, the Premium entitlement is granted
// server-side by the RevenueCat webhook, which is asynchronous. We poll the
// subscription query a few times until `isPremium` flips before declaring
// success. 6 requests, 5 pauses of 3 s ≈ 15 s covers the typical webhook
// round-trip; past that the page says the activation is still on its way.
const ENTITLEMENT_POLL_ATTEMPTS = 6
const ENTITLEMENT_POLL_INTERVAL_MS = 3000

// How often the page checks whether the wait for a late activation is over.
const ACTIVATION_WATCH_CHECK_MS = 10_000

// Below this viewport width the two tiers stack; at/above they sit side by side.
const SIDE_BY_SIDE_MIN_WIDTH = 768

const wait = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms))

// The server's built-in free limits, shown until GET /plan-limits answers.
const FALLBACK_FREE_LIMITS = { contacts: 500, ghosts: 30, notes: 10, reminders: 3 }

// Legal links shown on the paywall (App Store guideline 3.1.2). Terms of Use =
// Apple's standard EULA (always valid); privacy = our hosted policy.
const TERMS_OF_USE_URL = 'https://www.apple.com/legal/internet-services/itunes/dev/stdeula/'
const PRIVACY_POLICY_URL = 'https://keeptouch.co/privacy'

const APP_STORE_SUBSCRIPTIONS_URL = 'https://apps.apple.com/account/subscriptions'
const PLAY_STORE_SUBSCRIPTIONS_URL = 'https://play.google.com/store/account/subscriptions'

// Where a store subscription is stopped, keyed by the store `/me/subscription`
// reports. Used when RevenueCat gives no management page of its own.
const STORE_SUBSCRIPTIONS_URLS: Record<string, string> = {
  app_store: APP_STORE_SUBSCRIPTIONS_URL,
  mac_app_store: APP_STORE_SUBSCRIPTIONS_URL,
  play_store: PLAY_STORE_SUBSCRIPTIONS_URL,
}

// Infinity granted from the RevenueCat dashboard: nothing is billed.
const OFFERED_STORE = 'promotional'

// The "can't stop it from here" and "activation on its way" explanations run to
// two sentences.
const CANCEL_INFO_TOAST_MS = 6000

/**
 * Where "Oui, arrêter mon abonnement" sends the subscriber: RevenueCat's own
 * management page first, then the page of the store the subscription was
 * bought on, then — store unknown — the device's store. Null when there is
 * nowhere to go (offered access, a web page that could not be fetched).
 */
const resolveCancelUrl = (
  managementUrl: string | null,
  store: string | null,
  deviceStoreUrl: string | null,
): string | null => {
  if (managementUrl) {
    return managementUrl
  }
  if (store) {
    return STORE_SUBSCRIPTIONS_URLS[store] ?? null
  }
  return deviceStoreUrl
}

export const Subscription: React.FC = () => {
  const theme = useTheme()
  const styles = getSubscriptionStyles(theme)
  const navigation = useNavigation()
  const { isWeb, width } = useScreen()
  const { t } = useLanguage()
  const { isPremium, expiresAt, store, isActivationPending, refetch } = useSubscription()

  const { planLimits, isLoaded: arePlanLimitsLoaded } = usePlanLimits()

  const [priceLabel, setPriceLabel] = useState<string | null>(null)
  const [isPurchasing, setIsPurchasing] = useState(false)
  const [isActivating, setIsActivating] = useState(false)
  const [isRestoring, setIsRestoring] = useState(false)
  // A payment was accepted but the server has not confirmed it yet. Starts from
  // the shared watch so the buy button stays off when the page is reopened.
  const [isAwaitingActivation, setIsAwaitingActivation] = useState(Boolean(isActivationPending))
  const [isCancelModalVisible, setIsCancelModalVisible] = useState(false)
  const [managementUrl, setManagementUrl] = useState<string | null>(null)
  // Whether the subscription renews, as RevenueCat reports it; null = unknown.
  const [willRenew, setWillRenew] = useState<boolean | null>(null)

  // The native stores expose a self-serve subscription management page; the web
  // (Stripe/RevenueCat) has no equivalent deep link, so we hide the button there.
  const manageStoreUrl =
    Platform.OS === 'ios'
      ? APP_STORE_SUBSCRIPTIONS_URL
      : Platform.OS === 'android'
        ? PLAY_STORE_SUBSCRIPTIONS_URL
        : null

  const canPurchase = purchaseManager.isAvailable
  const isSideBySide = width >= SIDE_BY_SIDE_MIN_WIDTH

  const endDate = expiresAt ? fromApiDateFormat(expiresAt, 'DD/MM/YYYY') : null
  // The end date is a renewal only when RevenueCat says so: it is also the day
  // an offered access or a stopped subscription ends.
  const premiumMetaKey = !endDate
    ? 'subscription.premiumActive'
    : store === OFFERED_STORE
      ? 'subscription.offeredUntil'
      : willRenew === false
        ? 'subscription.endsOn'
        : willRenew === true
          ? 'subscription.expiresOn'
          : 'subscription.currentPeriodUntil'
  const premiumMetaText = t(premiumMetaKey, endDate ? { date: endDate } : undefined)

  // The free limits are the ones set in the admin, not numbers written in the texts.
  // Limits kept by the website from before the update lack the last two
  // numbers until the server answers again.
  const freeLimits = arePlanLimitsLoaded
    ? {
        contacts: planLimits.freeMaxContacts,
        ghosts: planLimits.freeMaxGhostProfiles ?? FALLBACK_FREE_LIMITS.ghosts,
        notes: planLimits.freeMaxNotes ?? FALLBACK_FREE_LIMITS.notes,
        reminders: planLimits.freeMaxKeepContactReminders,
      }
    : FALLBACK_FREE_LIMITS
  const freeFeatures = [
    t('subscription.freeFeatureContacts', { count: freeLimits.contacts }),
    t('subscription.freeFeatureGhosts', { count: freeLimits.ghosts }),
    t('subscription.freeFeatureNotes', { count: freeLimits.notes }),
  ]
  // No reminders line when the admin has switched the free reminders off.
  if (!arePlanLimitsLoaded || planLimits.freeKeepContactEnabled) {
    freeFeatures.push(t('subscription.freeFeatureReminders', { count: freeLimits.reminders }))
  }

  // Prefetch the offering so the Infinity price reflects the real store price
  // (web only). Missing key / offering degrades to the fallback label — never
  // throws.
  useEffect(() => {
    if (!canPurchase) {
      return
    }
    let isActive = true
    purchaseManager
      .getOffering()
      .then((offering) => {
        if (isActive && offering) {
          setPriceLabel(offering.priceLabel)
        }
      })
      .catch(() => undefined)
    return () => {
      isActive = false
    }
  }, [canPurchase])

  // Look up the page where this subscriber stops their subscription ahead of
  // "Oui", so the tap opens it straight away — a browser blocks a new tab opened
  // after waiting on the network. Looked up again whenever the confirmation
  // opens, as the first lookup can run before the purchase SDK is ready.
  useEffect(() => {
    if (!isPremium) {
      return
    }
    let isActive = true
    purchaseManager
      .getManagementUrl()
      .then((url) => {
        if (isActive) {
          setManagementUrl(url)
        }
      })
      .catch(() => undefined)
    // Same moments for the renewal: it changes on the page "Oui" opens.
    purchaseManager
      .getWillRenew()
      .then((renews) => {
        if (isActive) {
          setWillRenew(renews)
        }
      })
      .catch(() => undefined)
    return () => {
      isActive = false
    }
  }, [isPremium, isCancelModalVisible])

  // "Oui" opens the page where the subscription is stopped at the very moment
  // the confirmation closes: ask for the renewal again when the subscriber
  // comes back to the app or to the tab.
  useEffect(() => {
    if (!isPremium) {
      return
    }
    let isActive = true
    const listener = AppState.addEventListener('change', (state) => {
      if (state !== 'active') {
        return
      }
      purchaseManager
        .getWillRenew()
        .then((renews) => {
          if (isActive) {
            setWillRenew(renews)
          }
        })
        .catch(() => undefined)
    })
    return () => {
      isActive = false
      listener.remove()
    }
  }, [isPremium])

  // The server confirmed a payment the page was still waiting for.
  useEffect(() => {
    if (isAwaitingActivation && isPremium) {
      setIsAwaitingActivation(false)
      showSuccessToast(
        t('subscription.purchaseSuccessTitle'),
        t('subscription.purchaseSuccessMessage'),
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAwaitingActivation, isPremium])

  // A watch started while this page was already open (a payment made on
  // another copy of it): switch the buy button off here too. Not while this
  // page is itself waiting on its own payment, which reports its own outcome.
  useEffect(() => {
    if (isActivationPending && !isActivating) {
      setIsAwaitingActivation(true)
    }
  }, [isActivationPending, isActivating])

  // Once the watch is over without an answer, the buy button works again.
  useEffect(() => {
    if (!isAwaitingActivation) {
      return
    }
    const timer = setInterval(() => {
      if (!isSubscriptionActivationPending()) {
        setIsAwaitingActivation(false)
      }
    }, ACTIVATION_WATCH_CHECK_MS)
    return () => clearInterval(timer)
  }, [isAwaitingActivation])

  // Poll the subscription query until the webhook has granted the entitlement.
  // Resolves false when the server still says "free" after the last attempt.
  const waitForEntitlement = async (): Promise<boolean> => {
    for (let attempt = 0; attempt < ENTITLEMENT_POLL_ATTEMPTS; attempt += 1) {
      const result = await refetch()
      if (result.data?.isPremium) {
        return true
      }
      if (attempt < ENTITLEMENT_POLL_ATTEMPTS - 1) {
        await wait(ENTITLEMENT_POLL_INTERVAL_MS)
      }
    }
    return false
  }

  // The store took the payment but the server has not registered it yet: say
  // so instead of announcing an active subscription, and keep asking the server.
  const awaitActivation = (): void => {
    markSubscriptionActivationPending()
    setIsAwaitingActivation(true)
    showToast({
      type: 'info',
      title: t('subscription.activationPendingTitle'),
      message: t('subscription.activationPendingMessage'),
      visibilityTime: CANCEL_INFO_TOAST_MS,
    })
    // One more request, so the subscription query picks up its watch interval.
    void refetch()
  }

  const handleUpgrade = async (): Promise<void> => {
    // Native (Phase 3b) keeps the stub → isAvailable is false, so we only tell
    // the user it is coming. On web the purchase manager is wired for real.
    if (!canPurchase) {
      showInfoToast(t('subscription.comingSoon'), t('subscription.comingSoonMessage'))
      return
    }

    setIsPurchasing(true)
    try {
      const result = await purchaseManager.purchasePremium()
      if (result.ok) {
        setIsActivating(true)
        // The watch starts with the wait, so that a copy of this page opened
        // during these 15 s cannot take a second payment.
        markSubscriptionActivationPending()
        if (await waitForEntitlement()) {
          showSuccessToast(
            t('subscription.purchaseSuccessTitle'),
            t('subscription.purchaseSuccessMessage'),
          )
        } else {
          awaitActivation()
        }
      } else if (result.reason !== 'cancelled') {
        // A user cancellation is not an error — return silently. Anything else
        // surfaces a translated error toast.
        showErrorToast(t('subscription.purchaseErrorTitle'), t('subscription.purchaseErrorMessage'))
      }
    } finally {
      setIsActivating(false)
      setIsPurchasing(false)
    }
  }

  const handleRestore = async (): Promise<void> => {
    setIsRestoring(true)
    try {
      const result = await purchaseManager.restorePurchases()
      if (!result.ok) {
        showErrorToast(t('subscription.restoreErrorTitle'), t('subscription.restoreErrorMessage'))
        return
      }
      // When the store reports an active purchase, give the server the same
      // time to register it as after a payment; otherwise one check is enough.
      const isActive = result.hasActiveEntitlement
        ? await waitForEntitlement()
        : Boolean((await refetch()).data?.isPremium)
      if (isActive) {
        showSuccessToast(
          t('subscription.restoreSuccessTitle'),
          t('subscription.restoreSuccessMessage'),
        )
      } else if (result.hasActiveEntitlement) {
        awaitActivation()
      } else {
        showInfoToast(t('subscription.restoreNoneTitle'), t('subscription.restoreNoneMessage'))
      }
    } catch {
      showErrorToast(t('subscription.restoreErrorTitle'), t('subscription.restoreErrorMessage'))
    } finally {
      setIsRestoring(false)
    }
  }

  const handleManage = (): void => {
    if (!manageStoreUrl) {
      return
    }
    Linking.openURL(manageStoreUrl).catch(() => {
      showErrorToast(t('subscription.manageErrorTitle'), t('subscription.manageErrorMessage'))
    })
  }

  // "Oui" in the confirmation. Stores and RevenueCat alone can stop a
  // subscription, so this opens their page; the subscriber keeps Infinity until
  // the end of the period already paid.
  const handleConfirmCancel = (): void => {
    setIsCancelModalVisible(false)
    const cancelUrl = resolveCancelUrl(managementUrl, store, manageStoreUrl)
    if (!cancelUrl) {
      const isOffered = store === OFFERED_STORE
      showToast({
        type: 'info',
        title: t(
          isOffered ? 'subscription.cancelOfferedTitle' : 'subscription.cancelUnavailableTitle',
        ),
        message: t(
          isOffered ? 'subscription.cancelOfferedMessage' : 'subscription.cancelUnavailableMessage',
        ),
        visibilityTime: CANCEL_INFO_TOAST_MS,
      })
      return
    }
    Linking.openURL(cancelUrl).catch(() => {
      showErrorToast(t('subscription.manageErrorTitle'), t('subscription.manageErrorMessage'))
    })
  }

  const infinityPrice = priceLabel
    ? t('subscription.infinityPricePerMonth', { price: priceLabel })
    : t('subscription.infinityPriceFallback')

  const ctaLabel =
    isActivating || isAwaitingActivation
      ? t('subscription.activating')
      : isPurchasing
        ? t('subscription.processing')
        : t('subscription.infinityCta')

  return (
    <Screen>
      <Header
        title={t('subscription.title')}
        iconLeft={isWeb ? undefined : 'arrowBack'}
        onPressLeft={() => navigation.goBack()}
      />

      <ScrollView
        style={styles.container}
        contentContainerStyle={styles.contentContainer}
        showsVerticalScrollIndicator={false}
      >
        {isPremium ? (
          <View style={styles.premiumCard} testID="subscription-premium-state">
            <Icon name="star" size={theme.iconSize.jumbo} color={theme.colors.gold} />
            <Text type="titleBold" style={styles.premiumTitle}>
              {t('subscription.premiumTitle')}
            </Text>
            <Text type="body" style={styles.premiumSubtitle}>
              {t('subscription.premiumSubtitle')}
            </Text>
            <Text type="bodySmall" style={styles.premiumMeta}>
              {premiumMetaText}
            </Text>
            {manageStoreUrl ? (
              <Button
                testID="subscription-manage-cta"
                title={t('subscription.manageCta')}
                textColor={theme.colors.gold}
                style={styles.manageButton}
                onPress={handleManage}
              />
            ) : null}
            <Button
              testID="subscription-cancel-cta"
              title={t('subscription.cancelCta')}
              textColor={theme.colors.error}
              style={styles.cancelButton}
              onPress={() => setIsCancelModalVisible(true)}
            />
          </View>
        ) : (
          <>
            <View style={styles.hero}>
              <Text type="titleBigBold" center>
                {t('subscription.headline')}
              </Text>
              <Text type="body" style={styles.heroIntro}>
                {t('subscription.intro')}
              </Text>
            </View>

            <View style={[styles.tiers, isSideBySide && styles.tiersRow]}>
              <View style={isSideBySide ? styles.tierColumn : undefined}>
                <TierCard
                  testID="subscription-tier-free"
                  title={t('subscription.freeTierTitle')}
                  subtitle={t('subscription.freeTierSubtitle')}
                  price={t('subscription.freePriceLabel')}
                  features={freeFeatures}
                />
              </View>

              <View style={isSideBySide ? styles.tierColumn : undefined}>
                <TierCard
                  testID="subscription-tier-infinity"
                  highlighted
                  badgeLabel={t('subscription.infinityBadge')}
                  title={t('subscription.infinityTierTitle')}
                  subtitle={t('subscription.infinityTierSubtitle')}
                  price={infinityPrice}
                  features={INFINITY_FEATURE_KEYS.map((key) => t(key))}
                >
                  <Button
                    testID="subscription-upgrade-cta"
                    title={ctaLabel}
                    iconName="rocket"
                    iconColor={theme.colors.white}
                    textColor={theme.colors.white}
                    style={styles.ctaButton}
                    disabled={isPurchasing || isAwaitingActivation}
                    onPress={handleUpgrade}
                  />
                </TierCard>
              </View>
            </View>
          </>
        )}

        <Button
          testID="subscription-restore"
          title={isRestoring ? t('subscription.restoring') : t('subscription.restoreCta')}
          textColor={theme.colors.secondary}
          style={styles.restoreButton}
          disabled={isRestoring}
          onPress={handleRestore}
        />

        <View style={styles.legalFooter}>
          <Text type="bodySmall" style={styles.legalText}>
            {t('subscription.legalDisclosure')}
          </Text>
          <View style={styles.legalLinks}>
            <TouchableOpacity
              testID="subscription-terms-link"
              onPress={() => Linking.openURL(TERMS_OF_USE_URL)}
            >
              <Text type="bodySmall" style={styles.legalLink}>
                {t('subscription.termsOfUse')}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              testID="subscription-privacy-link"
              onPress={() => Linking.openURL(PRIVACY_POLICY_URL)}
            >
              <Text type="bodySmall" style={styles.legalLink}>
                {t('subscription.privacyPolicy')}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </ScrollView>

      <CancelSubscriptionModal
        isVisible={isCancelModalVisible}
        endDate={endDate}
        onKeep={() => setIsCancelModalVisible(false)}
        onConfirm={handleConfirmCancel}
      />
    </Screen>
  )
}
